import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { aboutContent, siteConfig } from '@/mocks/site';

const aboutUrl = `${siteConfig.url}/about`;
const aboutTitle = '关于我们 - 小辣椒影院 | 品牌介绍与联系方式';
const aboutDesc = '小辣椒影院成立于2020年，是国内领先的免费高清影视在线观看平台。了解小辣椒影院的品牌故事、用户口碑、服务特色及联系方式。';
const aboutKeywords = '小辣椒影院,关于小辣椒影院,小辣椒影院介绍,小辣椒影院联系方式,免费影视平台';

export default function AboutPage() {
  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={aboutTitle}
        description={aboutDesc}
        keywords={aboutKeywords}
        canonicalUrl={aboutUrl}
        schemaJsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于小辣椒影院',
            description: aboutDesc,
            url: aboutUrl,
            isPartOf: { '@type': 'WebSite', name: siteConfig.name, url: siteConfig.url },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '小辣椒影院', item: siteConfig.url },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: aboutUrl },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '小辣椒影院是否真的免费？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的，小辣椒影院所有影视内容完全免费在线观看，无需充值会员，无需付费。我们依靠广告收入维持运营，致力于为用户提供最优质免费的观影服务。',
                },
              },
              {
                '@type': 'Question',
                name: '支持哪些设备观看？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '小辣椒影院完美支持PC端、手机端、平板端等各种设备。您可以在电脑浏览器、手机浏览器以及平板设备上随时随地观看喜欢的内容。',
                },
              },
              {
                '@type': 'Question',
                name: '影片画质如何？多久更新一次？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '我们的所有影视资源均提供1080P以上高清画质，部分热门内容支持4K超清。内容运营团队每日更新最新影视资源，确保您第一时间观看到最新的热门内容。',
                },
              },
              {
                '@type': 'Question',
                name: '如何搜索想看的影片？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以在页面顶部的搜索框中输入影片名称、导演或演员姓名进行搜索。也可以在各个分类页面中按类型、评分、更新时间等条件筛选影片。',
                },
              },
              {
                '@type': 'Question',
                name: '遇到播放问题怎么办？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '如遇到播放卡顿或无法播放的情况，建议您先检查网络连接，或尝试切换浏览器。如问题持续存在，欢迎联系我们的客服团队获取帮助。',
                },
              },
            ],
          },
        ]}
      />
      <Navbar />
      <main className="min-h-screen pt-20">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto pt-6 pb-4">
            <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
              <a href="/" className="hover:text-primary-500 transition-colors">首页</a>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Page Header */}
        <section className="w-full px-4 md:px-6 pb-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">关于小辣椒影院</h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
              成立于{siteConfig.founded}年，致力于为每一位影视爱好者提供最优质的免费观影服务
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 pb-14">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {aboutContent.stats.map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6 text-center">品牌介绍</h2>
            <div className="bg-background-50 rounded-2xl p-6 md:p-8">
              {aboutContent.brandIntro.trim().split('\n\n').map((paragraph, i) => (
                <p key={i} className={`text-sm text-foreground-700 leading-relaxed ${i > 0 ? 'mt-4' : ''}`}>
                  {paragraph.trim()}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 py-14">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2 text-center">用户口碑</h2>
            <p className="text-sm text-foreground-500 mb-8 text-center">用品质赢得信赖，用服务收获口碑</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {aboutContent.reputation.map((item) => (
                <div key={item.title} className="bg-background-100 rounded-xl p-6 hover:bg-background-200/50 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-primary-500 text-xl w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-label text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2 text-center">联系我们</h2>
            <p className="text-sm text-foreground-500 mb-8 text-center">随时欢迎您的反馈和建议</p>
            <div className="bg-background-50 rounded-2xl p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500 mb-0.5">电子邮箱</div>
                    <strong className="text-sm text-foreground-800">{siteConfig.email}</strong>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500 mb-0.5">客服电话</div>
                    <strong className="text-sm text-foreground-800">{siteConfig.phone}</strong>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500 mb-0.5">公司地址</div>
                    <strong className="text-sm text-foreground-800">{siteConfig.address}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2 text-center">常见问题</h2>
            <p className="text-sm text-foreground-500 mb-8 text-center">关于小辣椒影院的常见疑问解答</p>
            <div className="flex flex-col gap-3">
              {[
                { q: '小辣椒影院是否真的免费？', a: '是的，小辣椒影院所有影视内容完全免费在线观看，无需充值会员，无需付费。我们依靠广告收入维持运营，致力于为用户提供最优质免费的观影服务。' },
                { q: '支持哪些设备观看？', a: '小辣椒影院完美支持PC端、手机端、平板端等各种设备。您可以在电脑浏览器、手机浏览器以及平板设备上随时随地观看喜欢的内容。' },
                { q: '影片画质如何？多久更新一次？', a: '我们的所有影视资源均提供1080P以上高清画质，部分热门内容支持4K超清。内容运营团队每日更新最新影视资源，确保您第一时间观看到最新的热门内容。' },
                { q: '如何搜索想看的影片？', a: '您可以在页面顶部的搜索框中输入影片名称、导演或演员姓名进行搜索。也可以在各个分类页面中按类型、评分、更新时间等条件筛选影片。' },
                { q: '遇到播放问题怎么办？', a: '如遇到播放卡顿或无法播放的情况，建议您先检查网络连接，或尝试切换浏览器。如问题持续存在，欢迎联系我们的客服团队获取帮助。' },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-200/50 transition-colors list-none">
                    <h4 className="font-label text-sm font-medium text-foreground-900 pr-4">{faq.q}</h4>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}